import { useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../i18n';
import { isEnabledLanguage } from '../i18n/languages';
import { authService } from '../services/authService';
import { User } from '../types/auth';
import { browserTimeZone } from '../utils/timeZone';
import { setActiveTimeZone } from '../utils/zonedTime';
import { logger } from '../utils/logger';

/**
 * Keeps the account's language/time zone and the UI in step:
 * - on sign-in the saved language wins over this device's (same app on every device);
 * - an account without saved values gets this device's language and time zone;
 * - changing the language in the UI (switcher or profile) saves it to the account;
 * - the account's time zone drives every date and "today" in the app (utils/zonedTime).
 */
export const useLocalePreferencesSync = () => {
  const { state, updateUser } = useAuth();
  const { language, setLanguage } = useTranslation();
  const user = state.user;
  // The account whose preferences were already pulled into the UI
  const syncedUserId = useRef<string | null>(null);
  // Last UI language seen, so only a real change is saved (not the first render)
  const previousLanguage = useRef(language);
  // Only a real user replaces the account state (a proxy error page must not blank the name)
  const saveUser = (updated: User | undefined) => {
    if (updated?.id) updateUser(updated);
  };

  // Dates, "today" and day counters use the account's zone (utils/zonedTime)
  setActiveTimeZone(user?.timezone);

  useEffect(() => {
    if (!user) {
      syncedUserId.current = null;
      return;
    }
    if (syncedUserId.current === user.id) return;
    syncedUserId.current = user.id;

    const missing: { language?: string; timezone?: string } = {};
    if (isEnabledLanguage(user.language)) {
      if (user.language !== language) setLanguage(user.language);
    } else {
      missing.language = language;
    }
    const zone = browserTimeZone();
    if (!user.timezone && zone) missing.timezone = zone;

    if (Object.keys(missing).length > 0) {
      authService.updateProfile(missing)
        .then(saveUser)
        .catch((error) => logger.error('Saving locale preferences failed', error));
    }
    // Runs once per signed-in account; language changes are handled below
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  useEffect(() => {
    if (previousLanguage.current === language) return;
    previousLanguage.current = language;
    if (!user || syncedUserId.current !== user.id || user.language === language) return;
    authService.updateProfile({ language })
      .then(saveUser)
      .catch((error) => logger.error('Saving language failed', error));
    // Only a UI language change should save; user updates come back through updateUser
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language]);
};
